import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {InputChangeEvent} from 'util/Types';

import ChangeStudyableAssignmentPermissionWarningBanner, {
  ChangeStudyableAssignmentPermissionOriginType,
} from './studyable/ChangeStudyableAssignmentPermissionWarningBanner';
import {useCanChangeStudyableAssignmentStudiesQuery} from './studyable/useCanChangeStudyableAssignmentQuery';
import {StudyAssociation} from './StudyableDAO';
import StudyAutoLinkingInput, {AutoLinkingProps, AutoLinkingStudy} from './StudyAutoLinkingInput';
import StudySearchInput, {StudySearchControlledItemsProps, StudySearchItem} from './StudySearchInput';

export interface StudyAssociationInputProps {
  studySearchControlledItemsProps: StudySearchControlledItemsProps;
  invalidItemNames: Array<string>;
  displayPermissionWarning?: boolean;
  // If omitted, no auto-linking controls are rendered.
  autoLinking?: AutoLinkingProps;
}

export default function StudyAssociationInput({
  studySearchControlledItemsProps,
  invalidItemNames,
  displayPermissionWarning,
  autoLinking,
}: StudyAssociationInputProps): JSX.Element {
  return (
    <>
      <StudySearchInput {...studySearchControlledItemsProps} shouldAllowMultiple={true} />
      {displayPermissionWarning && invalidItemNames.length > 0 && (
        <div className={css(styles.warningBanner)}>
          <ChangeStudyableAssignmentPermissionWarningBanner
            warningOriginType={ChangeStudyableAssignmentPermissionOriginType.STUDY}
            invalidStudyNames={invalidItemNames}
          />
        </div>
      )}
      {autoLinking && (
        <StudyAutoLinkingInput
          selectedStudies={studySearchControlledItemsProps.selectedItems}
          invalidItemNames={invalidItemNames}
          autoLinking={autoLinking}
        />
      )}
    </>
  );
}

/**
 * If `enableAutoLinkingSelection` is false, auto-linking is locked to `initialAutoLinkingStudy`, and deselecting it
 * disables auto-linking. If true, the user picks the auto-linking study from the selected studies, and deselecting it
 * moves auto-linking to another selected study.
 */
export function useStudyAssociationInputProps({
  initialStudySearchItems,
  displayPermissionWarning,
  initialAutoLinkingStudy,
  enableAutoLinkingSelection = false,
}: {
  initialStudySearchItems: Array<StudySearchItem>;
  displayPermissionWarning?: boolean;
  initialAutoLinkingStudy?: AutoLinkingStudy;
  enableAutoLinkingSelection?: boolean;
}): StudyAssociationInputProps {
  const lockedAutoLinkingStudy = enableAutoLinkingSelection ? undefined : initialAutoLinkingStudy;
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>(initialStudySearchItems);
  const [selectedAutoLinkingStudyId, setSelectedAutoLinkingStudyId] = useState<string | undefined>(
    initialAutoLinkingStudy?.id ?? initialStudySearchItems[0]?.id
  );
  const studyIds = studySearchItems.map((item) => item.id);
  const [isAutoLinkingChecked, setIsAutoLinkingChecked] = useState(
    initialAutoLinkingStudy !== undefined && studyIds.includes(initialAutoLinkingStudy.id)
  );
  // Get studies missing the change studyable assignment permission
  const studyNamesToStudyablePermission = useCanChangeStudyableAssignmentStudiesQuery(studyIds);
  const invalidItemNames = Array.from(studyNamesToStudyablePermission.entries())
    .filter(([, hasPermission]) => !hasPermission)
    .map(([name]) => name);
  const hasInvalidStudies = invalidItemNames.length > 0;

  const handleStudySearchItemsChange = (selectedStudies: Array<StudySearchItem>): void => {
    setStudySearchItems(selectedStudies);
    if (lockedAutoLinkingStudy) {
      if (!selectedStudies.some((study) => study.id === lockedAutoLinkingStudy.id)) {
        setIsAutoLinkingChecked(false);
      }
    } else if (!selectedStudies.some((study) => study.id === selectedAutoLinkingStudyId)) {
      setSelectedAutoLinkingStudyId(selectedStudies[0]?.id);
      if (selectedStudies.length === 0) {
        setIsAutoLinkingChecked(false);
      }
    }
  };
  const onCheckedChange = (e: InputChangeEvent): void => {
    setIsAutoLinkingChecked(e.target.checked);
  };

  let autoLinking: AutoLinkingProps | undefined;
  if (lockedAutoLinkingStudy) {
    autoLinking = {
      mode: 'locked',
      study: lockedAutoLinkingStudy,
      isChecked: isAutoLinkingChecked,
      isDisabled: !studyIds.includes(lockedAutoLinkingStudy.id) || hasInvalidStudies,
      onCheckedChange,
    };
  } else if (enableAutoLinkingSelection) {
    const selectedAutoLinkingStudy = studySearchItems.find(
      (study) => study.id === selectedAutoLinkingStudyId
    );
    if (selectedAutoLinkingStudy && BenchlingConfig.STUDIES_ENTRY_AUTO_LINKING_EXTENSIONS) {
      autoLinking = {
        mode: 'selectable',
        study: selectedAutoLinkingStudy,
        isChecked: isAutoLinkingChecked,
        isDisabled: hasInvalidStudies,
        onCheckedChange,
        onStudyChange: setSelectedAutoLinkingStudyId,
      };
    }
  }

  return {
    studySearchControlledItemsProps: {
      selectedItems: studySearchItems,
      onChange: handleStudySearchItemsChange,
    },
    invalidItemNames,
    displayPermissionWarning,
    autoLinking,
  };
}

export function getStudyAssociations({
  studySearchControlledItemsProps,
  autoLinking,
}: StudyAssociationInputProps): Array<StudyAssociation> {
  return studySearchControlledItemsProps.selectedItems.map((item) => ({
    studyId: item.id,
    isAutoLinking: autoLinking !== undefined && autoLinking.isChecked && item.id === autoLinking.study.id,
  }));
}

const styles = StyleSheet.create({
  warningBanner: {
    marginTop: 5,
  },
});
